<p-toast></p-toast>

<div class="lead-details-page p-fluid">
    <app-page-shell>

    @if (lead(); as activeLead) {
        <app-page-detail-hero [auditStamp]="{ createdAt: activeLead.createdAt, updatedAt: activeLead.updatedAt, createdBy: activeLead.createdByCustomerId, updatedBy: activeLead.updatedByCustomerId, createdByActor: activeLead.createdByActor, updatedByActor: activeLead.updatedByActor }"
            icon="pi pi-user"
            [eyebrow]="'CRM Lead ' + (activeLead.leadNumber || 'NEW')"
            [title]="activeLead.companyName"
            [subtitle]="(activeLead.pocName || 'No contact person') + (activeLead.pocEmail ? ' | ' + activeLead.pocEmail : '')">
            <div tags>
                <p-tag [value]="activeLead.status || 'NEW'" [severity]="getStatusSeverity(activeLead.status || 'NEW')"></p-tag>
                <p-tag [value]="activeLead.leadQuality || 'MEDIUM'" severity="info"></p-tag>
                <p-tag [value]="activeLead.businessType || 'Business type pending'" severity="secondary"></p-tag>
                @if (activeLead.roles?.length) {
                    @for (role of activeLead.roles; track role) {
                        <p-tag [value]="roleLabel(role)" severity="contrast"></p-tag>
                    }
                } @else {
                    <p-tag value="Roles Pending" severity="warn"></p-tag>
                }
                @if (activeLead.converted) {
                    <p-tag value="Converted" severity="success"></p-tag>
                }
            </div>
            <div actions>
                <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/crm/leads"></p-button>
                <p-button label="Create Quotation" icon="pi pi-file-plus" severity="secondary" [outlined]="true" (onClick)="createQuotation()"></p-button>
                <p-button label="Edit Lead" icon="pi pi-pencil" severity="info" [routerLink]="['/crm/leads', activeLead.id, 'edit']"></p-button>
                @if (!activeLead.converted) {
                    <p-button label="Convert to Prospect" icon="pi pi-sync" severity="success" [loading]="loadingConversion()" [disabled]="loadingConversion()" (onClick)="convertLeadToProspect()"></p-button>
                    <p-button label="Convert to Client" icon="pi pi-building" [loading]="loadingConversion()" [disabled]="loadingConversion()" (onClick)="openConvertDialog()"></p-button>
                } @else if (activeLead.convertedClientId) {
                    <p-button label="View Client" icon="pi pi-arrow-right" (onClick)="router.navigate(['/crm/clients', activeLead.convertedClientId])"></p-button>
                }
            </div>
        </app-page-detail-hero>

        <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

        <div class="lead-detail-grid">
            <section class="lead-profile-panel">
                <div class="section-header">
                    <div>
                        <h2>Lead Profile</h2>
                        <p>Customer intake details captured before conversion.</p>
                    </div>
                </div>

                <div class="info-group">
                    <div class="group-heading"><i class="pi pi-building"></i><span>Customer</span></div>
                    <div class="detail-list">
                        <div><span>Company</span><strong>{{ activeLead.companyName || '-' }}</strong></div>
                        <div><span>Local Name</span><strong>{{ activeLead.customerNameLocal || '-' }}</strong></div>
                        <div><span>Nature</span><strong>{{ activeLead.natureOfBusiness || '-' }}</strong></div>
                        <div><span>Website</span><strong>{{ activeLead.websiteURL || '-' }}</strong></div>
                    </div>
                </div>

                <div class="info-group">
                    <div class="group-heading"><i class="pi pi-tags"></i><span>Business Roles</span></div>
                    @if (activeLead.roles?.length) {
                        <div class="flex flex-wrap gap-2">
                            @for (role of activeLead.roles; track role) {
                                <p-tag [value]="roleLabel(role)" severity="contrast"></p-tag>
                            }
                        </div>
                    } @else {
                        <p class="text-600 m-0">No business roles assigned yet.</p>
                    }
                </div>

                <div class="info-group">
                    <div class="group-heading"><i class="pi pi-address-book"></i><span>Contact</span></div>
                    <div class="detail-list">
                        <div><span>Contact Person</span><strong>{{ activeLead.pocName || '-' }}</strong></div>
                        <div><span>Email</span><a [href]="'mailto:' + activeLead.pocEmail">{{ activeLead.pocEmail || '-' }}</a></div>
                        <div><span>Phone</span><strong>{{ activeLead.pocPhoneNumber || '-' }}</strong></div>
                        <div><span>Mobile</span><strong>{{ activeLead.mobileNumber || '-' }}</strong></div>
                    </div>
                </div>

                <div class="info-group">
                    <div class="group-heading"><i class="pi pi-map-marker"></i><span>Address</span></div>
                    <div class="detail-list">
                        <div><span>Street</span><strong>{{ activeLead.street1 || '-' }}</strong></div>
                        <div><span>Local Street</span><strong>{{ activeLead.street1Local || '-' }}</strong></div>
                        <div><span>City</span><strong>{{ activeLead.city || '-' }}</strong></div>
                        <div><span>Postal Code</span><strong>{{ activeLead.postalCode || '-' }}</strong></div>
                    </div>
                </div>

                <div class="info-group">
                    <div class="group-heading"><i class="pi pi-shield"></i><span>Compliance</span></div>
                    <div class="detail-list">
                        <div><span>VAT / Tax</span><strong>{{ activeLead.taxRegNumber || '-' }}</strong></div>
                        <div><span>CR Number</span><strong>{{ activeLead.crNumber || '-' }}</strong></div>
                        <div><span>Source</span><strong>{{ activeLead.source || '-' }}</strong></div>
                        <div><span>Sales Team</span><strong>{{ activeLead.salesTeamName || activeLead.kamCustomerId || 'Unassigned' }}</strong></div>
                    </div>
                </div>
            </section>

            <section class="lead-workspace-panel">
                <p-tabs [value]="activeTabValue" (valueChange)="onTabChange($event)">
                    <p-tablist>
                        <p-tab [value]="0">
                            <div class="tab-label">
                                <i class="pi pi-history"></i>
                                <span>Activity</span>
                            </div>
                        </p-tab>
                        <p-tab [value]="1">
                            <div class="tab-label">
                                <i class="pi pi-comments"></i>
                                <span>Notes</span>
                            </div>
                        </p-tab>
                        <p-tab [value]="2">
                            <div class="tab-label">
                                <i class="pi pi-calendar"></i>
                                <span>Reminders</span>
                            </div>
                        </p-tab>
                        <p-tab [value]="3">
                            <div class="tab-label">
                                <i class="pi pi-paperclip"></i>
                                <span>Attachments</span>
                            </div>
                        </p-tab>
                    </p-tablist>
                    
                    <p-tabpanels>
                        <p-tabpanel [value]="0">
                            <div class="workspace-header">
                                <div>
                                    <h2>Unified Activity Timeline</h2>
                                    <p>{{ activities().length }} activity records</p>
                                </div>
                                <div class="workspace-actions">
                                    <p-button label="Log Activity" icon="pi pi-plus" (onClick)="showCommDialog.set(true)"></p-button>
                                    @if (!activeLead.converted) {
                                        <p-button label="Convert to Prospect" icon="pi pi-sync" severity="success" [loading]="loadingConversion()" [disabled]="loadingConversion()" (onClick)="convertLeadToProspect()"></p-button>
                                        <p-button label="Convert to Client" icon="pi pi-building" [loading]="loadingConversion()" [disabled]="loadingConversion()" (onClick)="openConvertDialog()"></p-button>
                                    }
                                </div>
                            </div>
                            <p-timeline [value]="activities()" align="left" styleClass="customized-timeline">
                                <ng-template pTemplate="marker" let-log>
                                    <span class="timeline-marker" [class]="getActivitySeverity(log.activityType)">
                                        <i [class]="getActivityIcon(log.activityType)"></i>
                                    </span>
                                </ng-template>
                                <ng-template pTemplate="content" let-log>
                                    <div class="timeline-card">
                                        <div class="timeline-title">{{ log.title }}{{ log.status ? ' - ' + log.status : '' }}</div>
                                        <span>{{ log.occurredAt | date:'medium' }}</span>
                                        <p>{{ log.summary }}</p>
                                    </div>
                                </ng-template>
                            </p-timeline>
                            @if (activities().length === 0) {
                                <div class="empty-state">
                                    <i class="pi pi-history"></i>
                                    <strong>No activities recorded</strong>
                                    <span>Log calls, emails, and meetings as the lead progresses.</span>
                                </div>
                            }
                        </p-tabpanel>

                        <p-tabpanel [value]="1">
                            <div class="workspace-header">
                                <div>
                                    <h2>Internal Notes</h2>
                                    <p>{{ notes().length }} notes</p>
                                </div>
                                <div class="workspace-actions">
                                    <p-button label="Add Note" icon="pi pi-pencil" (onClick)="showNoteDialog.set(true)"></p-button>
                                </div>
                            </div>
                            <div class="notes-list">
                                @for (note of notes(); track note.id) {
                                    <article class="note-card">
                                        <span>{{ note.createdAt | date:'medium' }}</span>
                                        <p>{{ note.text }}</p>
                                    </article>
                                } @empty {
                                    <div class="empty-state">
                                        <i class="pi pi-comments"></i>
                                        <strong>No notes found</strong>
                                        <span>Add internal notes for handoff context.</span>
                                    </div>
                                }
                            </div>
                        </p-tabpanel>

                        <p-tabpanel [value]="2">
                            <div class="workspace-header">
                                <div>
                                    <h2>Follow-up Reminders</h2>
                                    <p>{{ reminders().length }} reminders</p>
                                </div>
                                <div class="workspace-actions">
                                    <p-button label="Set Reminder" icon="pi pi-calendar-plus" (onClick)="showReminderDialog.set(true)"></p-button>
                                </div>
                            </div>
                            <p-table [value]="reminders()" styleClass="p-datatable-sm reminders-table">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Task</th>
                                        <th>Due Date</th>
                                        <th>Status</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-rem>
                                    <tr>
                                        <td>{{ rem.reminderText }}</td>
                                        <td>{{ rem.dueDate | date:'medium' }}</td>
                                        <td>
                                            <p-tag [value]="rem.completed ? 'Completed' : 'Pending'" 
                                                   [severity]="rem.completed ? 'success' : 'warning'"></p-tag>
                                        </td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="emptymessage">
                                    <tr>
                                        <td colspan="3">
                                            <div class="empty-state compact">
                                                <i class="pi pi-calendar"></i>
                                                <strong>No reminders set</strong>
                                                <span>Create a follow-up task for this lead.</span>
                                            </div>
                                        </td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </p-tabpanel>

                        <p-tabpanel [value]="3">
                            <div class="workspace-header">
                                <div>
                                    <h2>Registration Attachments</h2>
                                    <p>{{ registrationAttachments().length }} files</p>
                                </div>
                                <div class="workspace-actions">
                                    <p-button label="Add Attachment" icon="pi pi-upload" (onClick)="showAttachmentDialog.set(true)"></p-button>
                                </div>
                            </div>
                            <p-table [value]="registrationAttachments()" styleClass="p-datatable-sm reminders-table">
                                <ng-template pTemplate="header">
                                    <tr><th>File Type</th><th>File</th><th>Uploaded</th><th></th></tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-attachment>
                                    <tr>
                                        <td>{{ registrationAttachmentLabel(attachment.category) }}</td>
                                        <td><a [href]="attachment.fileUrl" target="_blank">{{ attachment.fileName }}</a></td>
                                        <td>{{ attachment.uploadedAt ? (attachment.uploadedAt | date:'medium') : '-' }}</td>
                                        <td class="text-right"><button pButton icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger" (click)="deleteRegistrationAttachment(attachment)"></button></td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="emptymessage">
                                    <tr><td colspan="4"><div class="empty-state compact"><i class="pi pi-paperclip"></i><strong>No attachments found</strong><span>Add VAT, commercial registration, or national address files.</span></div></td></tr>
                                </ng-template>
                            </p-table>
                        </p-tabpanel>
                    </p-tabpanels>
                </p-tabs>
            </section>
        </div>
    } @else {
        <div class="loading-state">
            <p-progressSpinner styleClass="w-4rem h-4rem" strokeWidth="4" animationDuration=".5s"></p-progressSpinner>
            <p>Loading Lead Details...</p>
        </div>
    }
    </app-page-shell>
</div>

<!-- Add Note Dialog -->
<p-dialog [(visible)]="showNoteDialog" header="Add Internal Note" [modal]="true" [style]="{width: '400px'}">
    <div class="flex flex-column gap-2 mb-3">
        <label for="noteText" class="font-medium">Note Content</label>
        <textarea id="noteText" pInputTextarea [rows]="4" [autoResize]="true" 
                  [ngModel]="newNoteText()" (ngModelChange)="newNoteText.set($event)"
                  placeholder="Type your findings or updates here..."></textarea>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" [text]="true" (onClick)="showNoteDialog.set(false)"></p-button>
        <p-button label="Save Note" icon="pi pi-check" (onClick)="saveNote()" [disabled]="!newNoteText()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog [visible]="showAttachmentDialog()" (visibleChange)="showAttachmentDialog.set($event)" header="Add Registration Attachment" [modal]="true" [style]="{width: '520px'}">
    <div class="grid p-fluid">
        <div class="col-12">
            <p-select [options]="attachmentTypes" optionLabel="label" optionValue="value" [ngModel]="attachmentDraft().category" (ngModelChange)="updateAttachmentCategory($event)" styleClass="w-full"></p-select>
        </div>
        <div class="col-12">
            <label class="p-button p-component cursor-pointer">
                <i class="pi pi-upload mr-2"></i>
                <span>{{ uploadingAttachment() ? 'Uploading' : 'Choose File' }}</span>
                <input type="file" class="hidden" (change)="uploadRegistrationAttachment($event)" [disabled]="uploadingAttachment()">
            </label>
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Close" icon="pi pi-times" [text]="true" (onClick)="showAttachmentDialog.set(false)"></p-button>
    </ng-template>
</p-dialog>

<!-- Log Activity Dialog -->
<p-dialog
    [visible]="showCommDialog()"
    (visibleChange)="showCommDialog.set($event)"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="crm-activity-dialog"
    [style]="{ width: '640px', maxWidth: '94vw' }"
    [breakpoints]="{ '960px': '88vw', '640px': '94vw' }">
    <ng-template pTemplate="header">
        <div class="activity-dialog-header">
            <span class="activity-dialog-icon"><i class="pi pi-comments"></i></span>
            <div>
                <h3>Log Communication</h3>
                <p>Capture the latest call, email, or meeting update for this lead.</p>
            </div>
        </div>
    </ng-template>

    <div class="activity-dialog-body">
        <section class="activity-dialog-panel">
            <div class="activity-section-title"><i class="pi pi-send"></i><span>Communication Channel</span></div>
            <div class="activity-type-grid">
                <button type="button" class="activity-type-card" [class.active]="newCommLog().type === 'CALL'" (click)="updateCommType('CALL')">
                    <i class="pi pi-phone"></i><span>Call</span>
                </button>
                <button type="button" class="activity-type-card" [class.active]="newCommLog().type === 'EMAIL'" (click)="updateCommType('EMAIL')">
                    <i class="pi pi-envelope"></i><span>Email</span>
                </button>
                <button type="button" class="activity-type-card" [class.active]="newCommLog().type === 'MEETING'" (click)="updateCommType('MEETING')">
                    <i class="pi pi-users"></i><span>Meeting</span>
                </button>
            </div>
        </section>

        <section class="activity-dialog-panel">
            <div class="activity-form-grid">
                <div class="activity-field span-2">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <textarea id="leadCommSummary" pInputTextarea [rows]="4" [ngModel]="newCommLog().summary" (ngModelChange)="updateCommSummary($event)"></textarea>
                            <label for="leadCommSummary">Activity Summary</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>
                <div class="activity-field span-2">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar-clock"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-datePicker inputId="leadCommDate" [showTime]="true" [ngModel]="newCommLog().communicationDate" (ngModelChange)="updateCommDate($event)" appendTo="body" styleClass="w-full"></p-datePicker>
                            <label for="leadCommDate">Date & Time</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>
            </div>
        </section>
    </div>

    <ng-template pTemplate="footer">
        <div class="activity-dialog-footer">
            <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="showCommDialog.set(false)"></p-button>
            <p-button label="Log Activity" icon="pi pi-check" (onClick)="saveCommLog()" [disabled]="!newCommLog().summary"></p-button>
        </div>
    </ng-template>
</p-dialog>

<!-- Set Reminder Dialog -->
<p-dialog [(visible)]="showReminderDialog" header="Set Follow-up Reminder" [modal]="true" [style]="{width: '450px'}">
    <div class="grid p-fluid mt-1">
        <div class="col-12 mb-3">
            <label for="remTask" class="font-medium mb-1 block">Follow-up Task</label>
            <input id="remTask" pInputText [ngModel]="newReminder().reminderText" (ngModelChange)="updateReminderText($event)" placeholder="e.g. Call back to finalize price" />
        </div>
        <div class="col-12 mb-3">
            <label for="remDate" class="font-medium mb-1 block">Due Date</label>
            <p-datePicker id="remDate" [showTime]="true" [ngModel]="newReminder().dueDate" (ngModelChange)="updateReminderDate($event)"></p-datePicker>
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" [text]="true" (onClick)="showReminderDialog.set(false)"></p-button>
        <p-button label="Set Reminder" icon="pi pi-calendar-plus" (onClick)="saveReminder()" [disabled]="!newReminder().reminderText"></p-button>
    </ng-template>
</p-dialog>

<!-- Direct lead-to-client conversion retained for existing workflows. -->
<!-- Convert To Client Dialog -->
<p-dialog [(visible)]="conversionDialog" header="Convert Lead to Client" [modal]="true" [style]="{width: '980px'}" styleClass="lead-conversion-dialog" [draggable]="false" [resizable]="false">
    <form [formGroup]="conversionForm" class="conversion-shell p-fluid">
        <aside class="conversion-summary">
            <div class="summary-icon"><i class="pi pi-user"></i></div>
            <span class="summary-kicker">CRM conversion</span>
            <h3>{{ lead()?.companyName || 'Lead' }}</h3>
            <p>Review customer identity, compliance, address, and contact details before creating the client master record.</p>
            <div class="summary-facts">
                <div><span>Status</span><strong>{{ lead()?.status || 'NEW' }}</strong></div>
                <div><span>Quality</span><strong>{{ lead()?.leadQuality || 'MEDIUM' }}</strong></div>
                <div><span>Business type</span><strong>{{ lead()?.businessType || '-' }}</strong></div>
                <div><span>Sales owner</span><strong>{{ lead()?.salesTeamName || lead()?.kamCustomerId || 'Unassigned' }}</strong></div>
            </div>
        </aside>

        <section class="conversion-form-panel">
            <div class="conversion-section">
                <div class="conversion-section-title">
                    <i class="pi pi-sitemap"></i>
                    <span>Source & Ownership</span>
                </div>
                <div class="conversion-grid two">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-select inputId="existingCompanyPartyId" [options]="companyOptions()" optionLabel="label" optionValue="value" formControlName="existingCompanyPartyId" placeholder="Select existing company" appendTo="body" (onChange)="onExistingCompanyChange($event.value)"></p-select>
                            <label for="existingCompanyPartyId">Existing Company</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-select inputId="kamPartyId" [options]="employeeOptions()" optionLabel="label" optionValue="value" formControlName="kamPartyId" placeholder="Select employee" appendTo="body"></p-select>
                            <label for="kamPartyId">Employee</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>
            </div>

            <div class="conversion-section">
                <div class="conversion-section-title">
                    <i class="pi pi-id-card"></i>
                    <span>Company Details</span>
                </div>
                <div class="conversion-grid two">
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                            <p-iftaLabel><input id="companyName" pInputText formControlName="companyName" /><label for="companyName">Company Name</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('companyName')">{{ fieldError('companyName') }}</small>
                    </div>
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-language"></i></p-inputGroupAddon>
                            <p-iftaLabel><input id="customerNameLocal" pInputText formControlName="customerNameLocal" /><label for="customerNameLocal">Company Alternate Name</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('customerNameLocal')">{{ fieldError('customerNameLocal') }}</small>
                    </div>
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                            <p-iftaLabel><input id="crNumber" type="number" pInputText formControlName="crNumber" /><label for="crNumber">CR Number</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('crNumber')">{{ fieldError('crNumber') }}</small>
                    </div>
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-receipt"></i></p-inputGroupAddon>
                            <p-iftaLabel><input id="taxRegNumber" pInputText formControlName="taxRegNumber" /><label for="taxRegNumber">VAT / Tax Number</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('taxRegNumber')">{{ fieldError('taxRegNumber') }}</small>
                    </div>
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-globe"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="countryId" formControlName="countryId" [options]="countries()" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,countryCode" [showClear]="true" appendTo="body"></p-select>
                                <label for="countryId">Country</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('countryId')">{{ fieldError('countryId') }}</small>
                    </div>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="phoneNumber" pInputText formControlName="phoneNumber" /><label for="phoneNumber">Company Phone</label></p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-envelope"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="emailAddress" pInputText formControlName="emailAddress" /><label for="emailAddress">Company Email</label></p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-link"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="websiteURL" pInputText formControlName="websiteURL" /><label for="websiteURL">Website</label></p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="clientCategory" pInputText formControlName="clientCategory" /><label for="clientCategory">Client Category</label></p-iftaLabel>
                    </p-inputGroup>
                    <div class="conversion-field span-2 roles-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-objects-column"></i></p-inputGroupAddon>
                            <p-iftaLabel class="chips-ifta-label">
                                <p-multiSelect
                                    inputId="leadConversionRoles"
                                    formControlName="roles"
                                    [options]="crmRoles()"
                                    optionLabel="label"
                                    optionValue="value"
                                    display="chip"
                                    [filter]="true"
                                    [showClear]="true"
                                    appendTo="body"
                                    styleClass="w-full roles-multiselect">
                                </p-multiSelect>
                                <label for="leadConversionRoles">Business Roles *</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('roles')">{{ fieldError('roles') }}</small>
                    </div>
                </div>
            </div>

            <div class="conversion-section">
                <div class="conversion-section-title">
                    <i class="pi pi-briefcase"></i>
                    <span>Client Profile</span>
                </div>
                <div class="conversion-grid two">
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                            <p-iftaLabel><p-datePicker inputId="postingDate" formControlName="postingDate" appendTo="body"></p-datePicker><label for="postingDate">Posting Date</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('postingDate')">{{ fieldError('postingDate') }}</small>
                    </div>
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                            <p-iftaLabel><p-select inputId="businessType" formControlName="businessType" [options]="businessTypes" optionLabel="label" optionValue="value" appendTo="body"></p-select><label for="businessType">Business Type</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('businessType')">{{ fieldError('businessType') }}</small>
                    </div>
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-dollar"></i></p-inputGroupAddon>
                            <p-iftaLabel><input id="currencyCode" pInputText formControlName="currencyCode" /><label for="currencyCode">Currency</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('currencyCode')">{{ fieldError('currencyCode') }}</small>
                    </div>
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-percentage"></i></p-inputGroupAddon>
                            <p-iftaLabel><input id="exchangeRate" type="number" min="0" step="0.000001" pInputText formControlName="exchangeRate" /><label for="exchangeRate">Exchange Rate</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('exchangeRate')">{{ fieldError('exchangeRate') }}</small>
                    </div>
                    <p-inputGroup class="span-2">
                        <p-inputGroupAddon><i class="pi pi-compass"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="natureOfBusiness" pInputText formControlName="natureOfBusiness" /><label for="natureOfBusiness">Nature Of Business</label></p-iftaLabel>
                    </p-inputGroup>
                </div>
            </div>

            <div class="conversion-section">
                <div class="conversion-section-title">
                    <i class="pi pi-map-marker"></i>
                    <span>Address & Contact</span>
                </div>
                <div class="conversion-grid address">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="postalCode" pInputText formControlName="postalCode" /><label for="postalCode">Postal Code</label></p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-select inputId="cityId" formControlName="cityId" [options]="conversionCityOptions()" optionLabel="cityName" optionValue="id" [filter]="true" filterBy="cityName,country.name" [showClear]="true" appendTo="body" [disabled]="!conversionForm.get('countryId')?.value">
                                <ng-template pTemplate="selectedItem" let-city>{{ city ? cityLabel(city) : '' }}</ng-template>
                                <ng-template pTemplate="item" let-city>{{ cityLabel(city) }}</ng-template>
                            </p-select>
                            <label for="cityId">City</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                    <p-inputGroup class="span-2">
                        <p-inputGroupAddon><i class="pi pi-home"></i></p-inputGroupAddon>
                        <p-iftaLabel><textarea id="address" pInputTextarea [rows]="2" formControlName="address"></textarea><label for="address">Address</label></p-iftaLabel>
                    </p-inputGroup>
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                            <p-iftaLabel><input id="contactName" pInputText formControlName="contactName" /><label for="contactName">Contact Name</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('contactName')">{{ fieldError('contactName') }}</small>
                    </div>
                    <div class="conversion-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-at"></i></p-inputGroupAddon>
                            <p-iftaLabel><input id="contactEmail" pInputText formControlName="contactEmail" /><label for="contactEmail">Contact Email</label></p-iftaLabel>
                        </p-inputGroup>
                        <small class="p-error" *ngIf="fieldError('contactEmail')">{{ fieldError('contactEmail') }}</small>
                    </div>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-mobile"></i></p-inputGroupAddon>
                        <p-iftaLabel><input id="contactPhoneNumber" pInputText formControlName="contactPhoneNumber" /><label for="contactPhoneNumber">Contact Phone</label></p-iftaLabel>
                    </p-inputGroup>
                </div>
            </div>
        </section>
    </form>
    <ng-template pTemplate="footer">
        <p-button label="Reset" icon="pi pi-refresh" severity="secondary" [outlined]="true" (onClick)="openConvertDialog()"></p-button>
        <span class="conversion-footer-spacer"></span>
        <p-button label="Cancel" icon="pi pi-times" [text]="true" (onClick)="conversionDialog.set(false)"></p-button>
        <p-button label="Convert" icon="pi pi-check" severity="success" [loading]="loadingConversion()" [disabled]="loadingConversion()" (onClick)="convertLeadToClient()"></p-button>
    </ng-template>
</p-dialog>
